import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ChevronDown, ClipboardList } from 'lucide-react'

interface ActivityEntry {
  id: number
  coachId: number
  action: string
  target: string
  detail: Record<string, unknown> | null
  createdAt: number
}

/** 从结构化 detail 渲染当前语言的日志描述 */
function renderLogMessage(t: (key: string, options?: Record<string, unknown>) => string, entry: ActivityEntry): string {
  const d = entry.detail
  const clientName = d?.clientName as string | undefined
  const packageName = d?.packageName as string | undefined

  if (entry.action === 'auth.login') return t('settings.activity.authLogin')
  if (entry.action === 'settings.change_password') return t('settings.activity.changePassword')
  if (entry.action === 'client.create') return t('settings.activity.clientCreate').replace('{{name}}', clientName || '')
  if (entry.action === 'client.update') return t('settings.activity.clientUpdate').replace('{{name}}', clientName || '')
  if (entry.action === 'client.delete') return t('settings.activity.clientDelete').replace('{{name}}', clientName || '')
  if (entry.action === 'package.create') return t('settings.activity.packageCreate').replace('{{name}}', packageName || '').replace('{{client}}', clientName || '')
  if (entry.action === 'appointment.create') return t('settings.activity.appointmentCreate').replace('{{name}}', clientName || '')
  if (entry.action === 'appointment.complete') return t('settings.activity.appointmentComplete').replace('{{name}}', clientName || '')
  if (entry.action === 'appointment.no_show') return t('settings.activity.appointmentNoShow').replace('{{name}}', clientName || '')
  if (entry.action === 'appointment.delete') return t('settings.activity.appointmentDelete').replace('{{name}}', clientName || '')
  if (entry.action === 'appointment.move') return t('settings.activity.appointmentMove').replace('{{name}}', clientName || '')
  return t('settings.activity.unknown', { action: entry.action })
}

async function fetchActivityLogs(cursor?: number) {
  const qs = cursor ? `?limit=20&cursor=${cursor}` : '?limit=20'
  const res = await fetch(`/api/activity-logs${qs}`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch')
  return res.json() as Promise<{ items: ActivityEntry[]; nextCursor: number | null }>
}

export function ActivityLog() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['activity-logs'],
    queryFn: () => fetchActivityLogs(),
  })

  const items = data?.items || []
  const nextCursor = data?.nextCursor

  const handleLoadMore = async () => {
    if (!nextCursor) return
    try {
      const more = await fetchActivityLogs(nextCursor)
      qc.setQueryData<{ items: ActivityEntry[]; nextCursor: number | null }>(['activity-logs'], (old) => {
        if (!old) return more
        return {
          items: [...old.items, ...more.items],
          nextCursor: more.nextCursor,
        }
      })
    } catch { /* ignore */ }
  }

  return (
    <Card>
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" /> {t('settings.activity.title')}
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent>
          {isLoading && <p className="text-sm text-slate-400">{t('common.loading')}</p>}
          {isError && <p className="text-sm text-red-600">{t('common.failed')}</p>}
          {!isLoading && !isError && items.length === 0 && (
            <p className="text-sm text-slate-400">{t('settings.activity.empty')}</p>
          )}
          {items.length > 0 && (
            <ul className="space-y-1 text-sm" data-testid="activity-log-list">
              {items.map((entry) => (
                <li key={entry.id} className="flex justify-between py-1 border-b border-slate-100 last:border-0">
                  <span>{renderLogMessage(t, entry)}</span>
                  <span className="text-xs text-slate-400 shrink-0 ml-2">
                    {new Date(entry.createdAt * 1000).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {nextCursor && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2"
              onClick={handleLoadMore}
              data-testid="activity-log-load-more"
            >
              {t('common.loadMore')}
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  )
}
